import React, { useState } from 'react';

interface MessageMediaProps {
  mediaType: 'image' | 'video' | 'link';
  mediaUrl: string;
  thumbnailUrl?: string;
  linkPreview?: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
  };
}

export const MessageMedia: React.FC<MessageMediaProps> = ({
  mediaType,
  mediaUrl,
  thumbnailUrl,
  linkPreview
}) => {
  const [showFullImage, setShowFullImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  const handleImageClick = (url: string) => {
    setSelectedImage(url);
    setShowFullImage(true);
  };

  const handleDownload = async (url: string, type: 'image' | 'video') => {
    try {
      // Use relative URL for production, full URL for development
      const API_BASE_URL = process.env.NODE_ENV === 'production'
        ? ''
        : (process.env.REACT_APP_API_BASE_URL || '');

      const downloadUrl = `${API_BASE_URL}/api/auth/download-media?url=${encodeURIComponent(url)}&type=${type}`;
      console.log('Downloading from:', downloadUrl);

      const response = await fetch(downloadUrl, {
        credentials: 'include'
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download error response:', errorText);
        throw new Error(`Download request failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'type:', blob.type);

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const extension = type === 'image' ? 'jpg' : 'mp4';
      link.download = `BROODbook-${type}-${Date.now()}.${extension}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      console.log('Triggering download...');
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Failed to download ${type}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (mediaType === 'image') {
    return (
      <>
        <div
          style={{
            marginTop: '8px',
            cursor: 'pointer',
            borderRadius: '12px',
            overflow: 'hidden',
            maxWidth: '100%',
            position: 'relative'
          }}
        >
          <img
            src={mediaUrl}
            alt="Message attachment"
            style={{
              width: '100%',
              maxHeight: '400px',
              display: 'block',
              objectFit: 'cover',
              objectPosition: 'center',
              borderRadius: '12px',
              margin: '0 auto'
            }}
            onClick={() => handleImageClick(mediaUrl)}
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(mediaUrl, 'image');
            }}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'rgba(0, 0, 0, 0.6)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              padding: 0
            }}
            title="Download image"
          >
            <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
          </button>
        </div>

        {/* Full Image Modal */}
        {showFullImage && (
          <>
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
              }}
              onClick={() => setShowFullImage(false)}
            >
              <button
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '80px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(selectedImage, 'image');
                }}
                title="Download image"
              >
                <span className="material-icons">download</span>
              </button>
              <button
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white'
                }}
                onClick={() => setShowFullImage(false)}
              >
                <span className="material-icons">close</span>
              </button>
              <img
                src={selectedImage}
                alt="Full size"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </>
        )}
      </>
    );
  }

  if (mediaType === 'video') {
    return (
      <div
        style={{
          marginTop: '8px',
          borderRadius: '12px',
          overflow: 'hidden',
          maxWidth: '100%',
          position: 'relative'
        }}
      >
        <video
          controls
          style={{
            maxWidth: '100%',
            maxHeight: '300px',
            display: 'block',
            borderRadius: '12px'
          }}
          poster={thumbnailUrl}
        >
          <source src={mediaUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDownload(mediaUrl, 'video');
          }}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'rgba(0, 0, 0, 0.6)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
            padding: 0
          }}
          title="Download video"
        >
          <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
        </button>
      </div>
    );
  }

  if (mediaType === 'link' && linkPreview) {
    // Extract YouTube video ID for embedded player
    const isYouTube = linkPreview.url?.includes('youtube.com') || linkPreview.url?.includes('youtu.be');
    let youtubeId = '';

    if (isYouTube && linkPreview.url) {
      const match = linkPreview.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      youtubeId = match ? match[1] : '';
    }

    return (
      <a
        href={linkPreview.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          marginTop: '8px',
          display: 'block',
          textDecoration: 'none',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: 'var(--background-paper)',
          transition: 'transform 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        {isYouTube && youtubeId ? (
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
            />
          </div>
        ) : linkPreview.image ? (
          <img
            src={linkPreview.image}
            alt={linkPreview.title}
            style={{
              width: '100%',
              maxHeight: '200px',
              objectFit: 'cover',
              display: 'block'
            }}
          />
        ) : null}
        <div style={{ padding: '12px' }}>
          {linkPreview.title && (
            <div
              style={{
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '4px',
                fontSize: '14px'
              }}
            >
              {linkPreview.title}
            </div>
          )}
          {linkPreview.description && (
            <div
              style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {linkPreview.description}
            </div>
          )}
          {linkPreview.url && (
            <div
              style={{
                fontSize: '11px',
                color: 'var(--primary-color)',
                marginTop: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {new URL(linkPreview.url).hostname}
            </div>
          )}
        </div>
      </a>
    );
  }

  return null;
};
